<template>
  <div id="engine-display-condition">
    <div id="engine-input-period">
      <div class="title-area">
        <div class="title-text sdf-text-20-400-white">データ期間</div>
      </div>
      <div id="input-container">
        <template v-if="getMode === 'live'">
          <div class="mode-text-area sdf-text-22-400-white">
            現在のデータを取得しています
          </div>
          <div class="live-time-range-text-area">
            <feather-icon class="live-time-clock-icon" icon="ClockIcon" svgClasses="w-5 h-5"/>
            <span class="live-time-range-text sdf-text-18-400-white">最新{{liveDataTimeRange}}分</span>
          </div>
        </template>
        <template v-if="getMode === 'date'">
          <div class="mode-text-area sdf-text-22-400-white">
            {{selectedDate}} {{selectedStartTime}} - {{selectedDate}} {{selectedEndTime}}のデータを取得しています。
          </div>
        </template>
        <div class="mode-select-area">
          <vs-button class="mode-select-button date-button" color="#2D9CDB" @click="popupActive = !popupActive" type="filled">
            <div>
              <feather-icon class="calendar-icon" icon="CalendarIcon" svgClasses="w-5 h-5"/>
              <span class="mode-button-text sdf-text-18-400-white">
                日付を指定する
              </span>
            </div>
          </vs-button>
          <vs-button class="mode-select-button sdf-text-18-400-white" color="#2D9CDB" @click="submitLiveMode" type="filled">
            <div>
              <feather-icon class="clock-icon" icon="ClockIcon" svgClasses="w-5 h-5"/>
              <span class="mode-button-text sdf-text-18-400-white">
                ライブデータ
              </span>
            </div>
          </vs-button>
        </div>
        <transition name="fade">
          <div class="vs-popup" v-show="popupActive">
            <div class="vs-popup-container">

              <div class="date-selector">
                <div id="container">
                  <div class="date-picker-container">
                    <datepicker
                      name="selectTargetDate"
                      class="date-picker"
                      :language="ja"
                      :format="formatter"
                      :disabled-dates="disabledDates"
                      input-class="date-picker-inner sdf-text-18-400-white"
                      v-model="targetDate"
                    ></datepicker>
                    <span class="arrow"></span>
                  </div>
                  <input-period-slider
                    class="slider-container"
                    @getPeriod="getPeriod"
                    :minPeriod="1607439600000"
                    :maxPeriod="1607525999999">
                  </input-period-slider>
                  <vs-button @click="submitDateMode" class="get-data-button sdf-text-18-400-white" color="#2D9CDB">データを取得</vs-button>
                  <vs-button @click="popupActive = false" class="get-data-button sdf-text-18-400-white" color="transparent">キャンセル</vs-button>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div id="engine-category-items" v-if="showDataCategory">
      <div class="title-text sdf-text-20-400-white">
        データ種別
      </div>
      <div class="categories-area">
        <div
          class="category-item-container"
          v-for="(group, index) in filteredChannelGroupData" 
          :key="index"
        >
          <div
            class="category-item sdf-text-18-400-white"
            :class="{
              'last-item':index === filteredChannelGroupData.length-1,
              'first-item':index === 0,
              selected: group.channelGroup === getSelectedCategory,
              'sdf-text-18-700-blue': group.channelGroup === getSelectedCategory}"
          >
            <p
              @mousedown="channelGroupClicked(group.channelGroup)"
              class="category-text">{{group.categoryName}}
            </p>
          </div>
          <div v-if="index === 1" class="category-separator"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import SecureDataflowMixin from "@/mixins/SecureDataflowMixin"
import {createNamespacedHelpers} from "vuex"
import InputPeriodSlider from "./InputPeriodSlider"
import DatepickerMixin from "@/mixins/DatepickerMixin"
import Datepicker from "vuejs-datepicker"

const { mapGetters, mapActions, mapState } = createNamespacedHelpers("monitoringVesselEngine")

export default {
  name: "engineInputPeriod",
  mixins: [SecureDataflowMixin, DatepickerMixin],
  components: {
    InputPeriodSlider,
    Datepicker,
  },
  props: {
    showDataCategory: {
      type: Boolean,
      default: true
    },
    alertEventCallable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      popupActive: false,
      selectedDate: null,
      selectedStartTime: null,
      selectedEndTime: null,
      maxPeriod: moment().unix() * 1000,
      targetDate: moment().toDate(),
      disabledDates: {
        from: new Date()
      },
      startTime: "00:00:00",
      endTime: "23:59:59",

    }
  },
  computed: {
    ...mapGetters([
      "getChannelGroupData", "getVersion", "getMode","getCategoryId", "getCategoryNum", "getChannelTimeFrom", "getChannelTimeTo", "getChannelId",
      "getCategoryId", "getCategoryNum", "getChannelTimeFrom", "getChannelTimeTo", "getChannelId",
      "getSelectedCategory",
    ]),
    ...mapState({
      channelGroupData: state => state.channelGroupData,
      liveDataTimeRange: state => state.liveDataTimeRange,
      liveDataTimeRangeIndex: state => state.liveDataTimeRangeIndex,
    }),
    displayDate() {
      return moment(this.targetDate).format("YYYY/MM/DD");
    },
    filteredChannelGroupData() {
      return this.channelGroupData.filter(element => element.displayInTrendData)
    }
  },
  methods: {
    ...mapActions([
      "setModeState",
      "setChannelTimeFromState",
      "setChannelTimeToState",
      "getChannelGroupChannelsState",
      "getChannelMaster",
      "setChannelsDataSearchedByStatus",
      "clearLocalDbChannelData",
      "setSelectedCategoryState", 
      "resetAlertSummaryLiveDatas",
      "resetEventSummaryLiveDatas",
      "subscribeChannelDataEngine",
      "setOriginIdState",
      "initializeCustomChannelIds",
      "setReferencedChannelIdMap",
      "setZoomStoredRange",
      "resetZoomStoredRange",
      "resetZoomCount",
      "setLiveDataTimeRange",
      "resetLiveChannelTime"
    ]),

    /**
     * ライブデータを取得
     */
    async submitLiveMode(forceFlg) {
      if (this.getMode !== "live" || forceFlg === true) {

        this.openLoading();
        this.$nextTick(async () => {

          await this.clearLocalDbChannelData();
          await this.setReferencedChannelIdMap({});
          await this.resetZoomStoredRange()
          await this.resetLiveChannelTime()

          await this.setModeState("live").catch(error => {
            this.$vs.notify({
              title: `データ取得に失敗しました。時間をおいて再度お試しください。`,
              color:'warning',
              iconPack: "feather",
              icon: "icon-x-square"
            });
            this.$vs.loading.close('#content-area > .con-vs-loading');
            return Promise.reject(error)
          });
          await this.setChannelTime("live")
          if (this.alertEventCallable) await this.searchAlertEventSummary();

          this.$emit('refreshChannelData');
          this.$vs.loading.close('#content-area > .con-vs-loading');
        });
      } else if (this.getMode == "live" && this.liveDataTimeRangeIndex != 0) {
        // ズームされている状態でライブデータボタンを押すとズーム値を元に戻す
        this.$emit('resetLiveChannelZoom')
      }
    },

    /**
     * 他コンポーネントから時間表示操作
     */
    setTimeDisplay(startTime, endTime) {
      this.selectedStartTime = startTime;
      this.selectedEndTime   = endTime;
    },

    /**
     * Dateモードでデータを取得
     */
    async submitDateMode() {

      this.openLoading();
      this.$nextTick(async () => {

        await this.clearLocalDbChannelData();
        await this.setReferencedChannelIdMap({});

        this.selectedDate      = this.displayDate;
        this.selectedStartTime = this.startTime;
        this.selectedEndTime   = this.endTime;
        this.popupActive = false;
        
        await this.setModeState("date").catch(error => {
          this.$vs.notify({
            title: `データ取得に失敗しました。時間をおいて再度お試しください。`,
            color:'warning',
            iconPack: "feather",
            icon: "icon-x-square"
          });
          this.setZoomStoredRange() // データ取得期間を保存
          this.resetZoomCount()
          this.$vs.loading.close('#content-area > .con-vs-loading');
          return Promise.reject(error)
        });
        await this.setChannelTime("date");
        if (this.alertEventCallable) await this.searchAlertEventSummary();

        await this.setZoomStoredRange() // データ取得期間を保存
        await this.resetZoomCount()

        this.$emit('refreshChannelData');
        this.$vs.loading.close('#content-area > .con-vs-loading');
      });
    },

    /**
     * データ種別を変更
     */
    channelGroupClicked(channelGroup) {

      this.openLoading();
      this.$nextTick(async () => {

        await this.setChannelTime(this.getMode);
        await this.setSelectedCategoryState(channelGroup);

        this.$emit('refreshChannelData');
        this.$vs.loading.close('#content-area > .con-vs-loading');
      });
    },


    /**
     * モードに合わせて開始終了時間をstate保存
     */
    async setChannelTime(mode) {
      let time_from = null;
      let time_to = null;
      if (mode === "live") {
        let liveDataTimeRange = this.liveDataTimeRange ? this.liveDataTimeRange : 60;
        time_from = moment().subtract(liveDataTimeRange, "minutes").format("YYYY-MM-DDTHH:mm:ss.000");
        time_to   = moment().format("YYYY-MM-DDTHH:mm:ss.000");
      } else {
        time_from = moment(this.targetDate).format("YYYY-MM-DD") + "T" + this.startTime + ".000";
        time_to   = moment(this.targetDate).format("YYYY-MM-DD") + "T" + this.endTime   + ".000";
      }

      await this.setChannelTimeFromState(moment(time_from).toISOString());
      await this.setChannelTimeToState(  moment(time_to  ).toISOString());
    },


    /**
     * アラートサマリー、イベントサマリーの再取得
     */
    async searchAlertEventSummary() {
      let promiseAll = [];

      promiseAll.push(new Promise( async ( resolve ) => { 
        // アラートサマリーのデータを取得
        await this.resetAlertSummaryLiveDatas();
        let payload = {
          status_contains: ["S", "*"],
          channel_group: "alert",
        }
        resolve(this.setChannelsDataSearchedByStatus(payload)); 
      }));
      promiseAll.push(new Promise( async ( resolve ) => { 
        // イベントサマリーのデータを取得
        await this.resetEventSummaryLiveDatas();
        let payload = {
          status_contains: ["E"],
          channel_group: "event",
        }
        resolve(this.setChannelsDataSearchedByStatus(payload)); 
      }));

      await Promise.all(promiseAll);
    },


    /**
     *  input-period-sliderの変化を受けて、日付指定ダイアログの 開始時間--終了時間をリアクティブに表示
     */
    getPeriod(periods) {
      this.startTime = moment(periods.startPeriod).local().format("HH:mm:ss")
      this.endTime   = moment(periods.endPeriod  ).local().format("HH:mm:ss")
    },

    /**
     * ローディング (最長20秒)
     */
    openLoading(){
      this.$vs.loading({
        text: "データ取得中",
        textAfter: true,
        color: this.sdfColor.Blue,
        container: '#content-area',
      })
    }
  },
  async created() {
    await this.clearLocalDbChannelData();
    await this.setReferencedChannelIdMap({});

    this.setOriginIdState();
    this.resetAlertSummaryLiveDatas();
    this.resetEventSummaryLiveDatas();
    await this.getChannelMaster();

    // URLパラメータからチャンネルグループを初期化
    if (this.$route.query.channelGroup) {
      const channelGroupList = this.getChannelGroupData.map(element => element.channelGroup)
      const targetChannelGroup = this.$route.query.channelGroup
      if (channelGroupList.includes(targetChannelGroup)) this.setSelectedCategoryState(targetChannelGroup)
    }

    // 初期表示はライブモード。
    this.submitLiveMode(true);

  },
  beforeDestroy() {
    // localDbのchannelDataをクリア
    this.clearLocalDbChannelData();
    this.setReferencedChannelIdMap({});
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/secure-dataflow/_variables.scss";
.title-loading {
  color: $white;
  font-size: 30px;
}
#engine-input-period {
  background-color: #112935;
  padding: 30px;
  color: $white;
  display: flex;
  flex-direction: row;
  align-items: center;

  .selected {
    background: $blue !important;
    color: $white !important;
  }

  .title-text {
    width: 250px;
  }

  .title-area {
    min-width: 220px;
  }

  #input-container {
    width: 100%;
    display: flex;
    flex: 1;
  }

  .master-container {
    height: 150px;
  }

  .period-text {
    margin-bottom: 15px;
  }

  .calendar-icon, .clock-icon {
    position: absolute;
    margin-top: 4px;
  }

  .mode-select-button {
    padding: 4px 16px !important; 
    margin-right: 10px;
    border-radius: 4px;
  }

  .mode-button-text {
    padding-left: 30px;
  }

  .mode-text-area {
    float: left;
    margin-right: 25px;
  }
  .live-time-range-text-area {
    display: flex;
    align-items: center;
    .live-time-clock-icon {
      margin-right: 5px;
    }
  }

  .mode-select-area {
    //float: right;
    margin-left: auto;
  }

  .zoom-icon {
    cursor: pointer;
  }

  .zoom-button-container {
    display: flex;
    align-items: center;
  }

  .vs-button--text {
    display: flex !important;
  }

  .vs-popup--background {
      width: 100%;
      background: rgba(0,0,0,.4);
      position: absolute;
      top: 0;
      height: 100%;
      z-index: 10;
      -webkit-transition: all .25s ease;
      transition: all .25s ease;
      opacity: 1;
  }

  .vs-popup {
      left: 0;
      transition: all .2s;
      width: 100%;
      height: 100%;
      position: fixed;
      top: 0;
      z-index: 20000;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
  }

  .vs-popup-container {
    z-index: 11;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .3s;
  }

  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
}
.date-selector {
  height: 300px;
  width: 1170px;
  left: 0;
  border-radius: 6px;
  padding: 15px;
  background: #21556F;
  margin-left: auto;
  margin-right: auto;

  .date-picker-container {
    display: inline;
  }
  
  .arrow{
    z-index: 2;
    border-color: $navy;
    border-style: solid;
    border-width: 10px;
    border-left-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
    margin-left: -30px;
    margin-bottom: 10px;
    position: absolute;
    margin-top: 22px;
    pointer-events: none;
  }


  .date-picker {
    width: 140px;
    display: inline-block;
  }

  .date-picker-inner {
    padding: 6px;
  }

  .date-picker input {
    border: 2px solid $navy;
    background: transparent;
    font-size: 18px;
    color: $white;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .vdp-datepicker__calendar {
    background: $navy;
    border: transparent;

    .prev:hover, .up:hover, .next:hover {
      background: $blue !important;
    }

    .cell:hover {
      background: $blue !important;
    }

    .disabled {
      color: #f2f2f232;
    }

    .selected{
      background: $blue !important;

    }
  }

  .vs-button {
    padding: 4px 18px !important;
  }

  .time-range-text {
    margin-bottom: 20px;
  }
}

#engine-category-items {
  background-color: #204557;
  padding-left: 30px;
  color: $white;
  display: flex;
  align-items: center;
}

#engine-category-items {
  background-color: #204557;
  padding-left: 30px;
  color: $white;
  display: flex;
  align-items: center;
}

.categories-area {
  display: flex;
  overflow-x: auto;
  width: 100%;
  height: 54px;
  align-items: center;
}

.category-item-container {
  display: flex;
  align-items: center;
}

.category-item {
  height: 54px;
  font-size: 16px;
  margin-left: 20px;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.first-item {
  margin-left: 0px;
}

.category-separator {
  border-right: 1px solid;
  height: 30px;
}

.category-text:hover {
  color: $blue;
}

.selected {
  padding-top: 4px;
  border-bottom: solid 4px;
}
.category-text {
  cursor: pointer;
  white-space: nowrap;
}

.title-text {
  padding-top: 12px;
  padding-bottom: 12px;
  min-width: 220px;
}

</style>
